import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { EDIT_PARTICIPANT_ROLES, hasRoles } from '../../../../../util/RoleUtil';

import { viewStaffForStudy } from '../../../../../services/PersonService';
import {
  deleteParticipantPastVisit,
  updateParticipantPastVisit,
  viewParticipant,
} from '../../../../../services/ParticipantService';

import { formatDateAsString } from '../../../../../util/StringUtil';

import { SaveButton } from '../../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../../common/checkbox/Checkbox';

import './PastVisit.css';

export const PastVisit = ({
  participant,
  setParticipant,
  setSearchResults,
  visit,
  setShowVisitDetails,
  context,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
}) => {
  const [visitDate, setVisitDate] = useState(null);
  const [visitCode, setVisitCode] = useState('');
  const [subVisitCode, setSubVisitCode] = useState('');
  const [visitType, setVisitType] = useState('');
  const [formsList, setFormsList] = useState([]);
  const [dataCapturedDate, setDataCapturedDate] = useState(null);
  const [dataCapturedBy, setDataCapturedBy] = useState(null);
  const [sdvNeeded, setSdvNeeded] = useState(false);
  const [sdvCapturedDate, setSdvCapturedDate] = useState(null);
  const [sdvCapturedBy, setSdvCapturedBy] = useState(null);
  const [personList, setPersonList] = useState([]);
  const [dataCapturedDateError, setDataCapturedDateError] = useState('');
  const [dataCapturedByError, setDataCapturedByError] = useState('');
  const [protocolDeviationDate, setProtocolDeviationDate] = useState();
  const [protocolDeviationDateError, setProtocolDeviationDateError] = useState('');
  const [protocolDeviationBy, setProtocolDeviationBy] = useState();
  const [protocolDeviationByError, setProtocolDeviationByError] = useState('');
  const [missedVisitCRFDate, setMissedVisitCRFDate] = useState();
  const [missedVisitCRFDateError, setMissedVisitCRFDateError] = useState('');
  const [missedVisitCRFBy, setMissedVisitCRFBy] = useState();
  const [missedVisitCRFByError, setMissedVisitCRFByError] = useState('');
  const [sdvCapturedDateError, setSdvCapturedDateError] = useState('');
  const [sdvCapturedByError, setSdvCapturedByError] = useState('');

  const retrieveDropdownUsers = useCallback(async () => {
    setSpinnerText('Retrieving Users');

    const payload = {
      participantExternalReference: participant.externalReference,
    };
    const [data, error] = await viewStaffForStudy(context.username, payload);

    if (data) {
      if (data.responseStatus === 'Success') {
        setPersonList(data.viewAllPersonSystemUserVOs);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.username, setSpinnerText, showToaster]);

  useEffect(() => {
    retrieveDropdownUsers();

    if (!visit) return;

    const {
      visitDate,
      visitCode,
      subVisitCode,
      visitType,
      forms,
      sdvNeeded,
      dataCapturedOn,
      dataCapturedBy,
      dataCapturedByExternalReference,
      sdvCompletedOn,
      sdvCompletedBy,
      sdvCompletedByExternalReference,
      protocolDeviationDate,
      protocolDeviationBy,
      protocolDeviationByExternalReference,
      missedVisitCrfDate,
      missedVisitCrfBy,
      missedVisitCrfByExternalReference,
    } = visit;

    setVisitDate(visitDate ? new Date(visitDate) : null);
    setVisitCode(visitCode || 'N/A');
    setSubVisitCode(subVisitCode || 'N/A');
    setVisitType(visitType || 'N/A');
    setFormsList(forms || []);
    setSdvNeeded(sdvNeeded || false);

    setDataCapturedDate(dataCapturedOn ? new Date(dataCapturedOn) : null);
    if (dataCapturedBy && dataCapturedByExternalReference) {
      setDataCapturedBy({
        name: dataCapturedBy,
        externalReference: dataCapturedByExternalReference,
      });
    }

    setSdvCapturedDate(sdvCompletedOn ? new Date(sdvCompletedOn) : null);
    if (sdvCompletedBy && sdvCompletedByExternalReference) {
      setSdvCapturedBy({
        name: sdvCompletedBy,
        externalReference: sdvCompletedByExternalReference,
      });
    }

    setProtocolDeviationDate(protocolDeviationDate ? new Date(protocolDeviationDate) : null);
    if (protocolDeviationBy && protocolDeviationByExternalReference) {
      setProtocolDeviationBy({
        name: protocolDeviationBy,
        externalReference: protocolDeviationByExternalReference,
      });
    }

    setMissedVisitCRFDate(missedVisitCrfDate ? new Date(missedVisitCrfDate) : null);
    if (missedVisitCrfBy && missedVisitCrfByExternalReference) {
      setMissedVisitCRFBy({
        name: missedVisitCrfBy,
        externalReference: missedVisitCrfByExternalReference,
      });
    }
  }, [retrieveDropdownUsers, visit]);

  const handleDeleteVisitClick = () => {
    const deleteVisitExtRef = {
      visitExternalReference: visit.visitExternalReference,
    };
    deleteVisit(deleteVisitExtRef);
  };

  const handleCancelClick = () => {
    setShowVisitDetails(false);
  };

  const validateMissedVisitFields = (valid, currentDate) => {
    const { protocolDeviation, missedVisitCrf } = visit;

    if (protocolDeviation) {
      if (protocolDeviationDate && protocolDeviationDate > currentDate) {
        setProtocolDeviationDateError('Date cannot be future dated.');
        valid = false;
      } else if (protocolDeviationBy && !protocolDeviationDate) {
        setProtocolDeviationDateError('*REQUIRED');
        valid = false;
      } else {
        setProtocolDeviationDateError('');
      }

      if (protocolDeviationDate && !protocolDeviationBy) {
        setProtocolDeviationByError('*REQUIRED');
        valid = false;
      } else {
        setProtocolDeviationByError('');
      }
    }

    if (missedVisitCrf) {
      if (missedVisitCRFDate && missedVisitCRFDate > currentDate) {
        setMissedVisitCRFDateError('Date cannot be future dated.');
        valid = false;
      } else if (missedVisitCRFBy && !missedVisitCRFDate) {
        setMissedVisitCRFDateError('*REQUIRED');
        valid = false;
      } else {
        setMissedVisitCRFDateError('');
      }

      if (missedVisitCRFDate && !missedVisitCRFBy) {
        setMissedVisitCRFByError('*REQUIRED');
        valid = false;
      } else {
        setMissedVisitCRFByError('');
      }
    }

    return valid;
  };

  const validateFields = () => {
    const currentDate = new Date();
    let valid = true;

    const { missedVisit } = visit;

    if (!missedVisit) {
      if (dataCapturedDate && dataCapturedDate > currentDate) {
        setDataCapturedDateError('Date cannot be future dated.');
        valid = false;
      } else if (dataCapturedBy && !dataCapturedDate) {
        setDataCapturedDateError('*REQUIRED');
        valid = false;
      } else {
        setDataCapturedDateError('');
      }

      if (dataCapturedDate && !dataCapturedBy) {
        setDataCapturedByError('*REQUIRED');
        valid = false;
      } else {
        setDataCapturedByError('');
      }
    } else {
      valid = validateMissedVisitFields(valid, currentDate);
    }

    if (sdvNeeded && sdvCapturedDate && sdvCapturedDate > currentDate) {
      setSdvCapturedDateError('Date cannot be future dated.');
      valid = false;
    } else if (sdvNeeded && !sdvCapturedDate && sdvCapturedBy) {
      setSdvCapturedDateError('*REQUIRED');
      valid = false;
    } else {
      setSdvCapturedDateError('');
    }

    if (sdvNeeded && !sdvCapturedBy && sdvCapturedDate) {
      setSdvCapturedByError('*REQUIRED');
      valid = false;
    } else {
      setSdvCapturedByError('');
    }

    return valid;
  };

  const handleSaveClick = () => {
    const validated = validateFields();
    if (!validated) {
      return;
    }

    const updateVisitVO = {
      updateVisitVO: {
        dataCapturedByExternalReference: dataCapturedBy?.externalReference,
        dataCapturedOn: formatDateAsString(dataCapturedDate),
        sdvCapturedByExternalReference: sdvNeeded ? sdvCapturedBy?.externalReference : null,
        sdvCapturedOn: sdvNeeded ? formatDateAsString(sdvCapturedDate) : null,
        missedVisitCrfByExternalReference: missedVisitCRFBy?.externalReference,
        missedVisitCrfDate: formatDateAsString(missedVisitCRFDate),
        protocolDeviationByExternalReference: protocolDeviationBy?.externalReference,
        protocolDeviationDate: formatDateAsString(protocolDeviationDate),
        visitExternalReference: visit.visitExternalReference,
        sdvNeeded,
      },
    };

    updateVisit(updateVisitVO);
  };

  const updateVisit = async (updateVisitRequest) => {
    setSpinnerText('Updating Visit');
    const [data, error] = await updateParticipantPastVisit(context.username, updateVisitRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Visit successfully updated.',
        });
        setSpinnerText(null);
        setShowVisitDetails(false);
        await retrieveParticipant(context.username, participant.externalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const retrieveParticipant = async (username, externalReference) => {
    setSpinnerText('Retrieving Participant');
    const [data, error] = await viewParticipant(username, externalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        setParticipant(data.participant);
        await setSearchResults(data.participant?.pastVisits);
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Participant Information Retrieved',
        });
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
  };

  const deleteVisit = async (deleteVisitRequest) => {
    setSpinnerText('Deleting Visit');
    const [data, error] = await deleteParticipantPastVisit(context.username, deleteVisitRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Visit successfully deleted.',
        });
        setShowVisitDetails(false);
        await retrieveParticipant(context.username, participant.externalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleCheckboxChange = (value) => {
    setSdvNeeded(value);
  };

  const fieldsDisabled = !hasRoles(context, EDIT_PARTICIPANT_ROLES);

  const renderDataCaptureFields = () => (
    <>
      <div className="input-container" style={{ marginTop: '5px' }}>
        <label className="input-label-right">Data Captured on: </label>
        <DatePicker
          selected={dataCapturedDate}
          className={`text-input ${dataCapturedDateError && 'error-border'}`}
          dateFormat="dd-MM-yyyy"
          showPopperArrow={false}
          dropdownMode="select"
          placeholderText="Select..."
          disabled={fieldsDisabled}
          onChange={(selectedDate) => setDataCapturedDate(selectedDate)}
          showYearDropdown
          showMonthDropdown
        />
        <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
        {toolTipsEnabled && (
          <ToolTip
            tooltipExternalReference="dataCapturedData123"
            context={context}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            fieldName="Data Captured on:"
          />
        )}
      </div>
      <div className="error-message">
        {dataCapturedDateError && <div className="small-red-text">{dataCapturedDateError}</div>}
      </div>
      <div className="input-container" style={{ marginTop: '5px' }}>
        <label className="input-label-right">Data Captured by: </label>
        <Select
          options={personList}
          onChange={setDataCapturedBy}
          value={dataCapturedBy || null}
          isDisabled={fieldsDisabled}
          classNamePrefix={`input-select${dataCapturedByError ? 'error-border' : ''}`}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.externalReference}
          closeMenuOnSelect
          isClearable
        />
        {toolTipsEnabled && (
          <ToolTip
            tooltipExternalReference="dataCapturedBy123"
            context={context}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            fieldName="Data Captured by:"
          />
        )}
      </div>
      <div className="error-message">
        {dataCapturedByError && <div className="small-red-text">{dataCapturedByError}</div>}
      </div>
    </>
  );

  const renderMissedVisitFields = () => {
    const { protocolDeviation, missedVisitCrf } = visit;

    return (
      <>
        {protocolDeviation && (
          <>
            <div className="input-container" style={{ marginTop: '5px' }}>
              <label className="input-label-right">Protocol Deviation Date:</label>
              <DatePicker
                selected={protocolDeviationDate}
                className={`text-input ${protocolDeviationDateError && 'error-border'}`}
                dateFormat="dd-MM-yyyy"
                showPopperArrow={false}
                dropdownMode="select"
                placeholderText="Select..."
                disabled={fieldsDisabled}
                onChange={(selectedDate) => setProtocolDeviationDate(selectedDate)}
                showYearDropdown
                showMonthDropdown
              />
              <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference="protocolDeviationDate"
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName="Protocol Deviation Date:"
                />
              )}
            </div>
            <div className="error-message">
              {protocolDeviationDateError && <div className="small-red-text">{protocolDeviationDateError}</div>}
            </div>

            <div className="input-container" style={{ marginTop: '5px' }}>
              <label className="input-label-right">Protocol Deviation by:</label>
              <Select
                options={personList}
                onChange={setProtocolDeviationBy}
                value={protocolDeviationBy}
                isDisabled={fieldsDisabled}
                classNamePrefix={`input-select${protocolDeviationByError ? 'error-border' : ''}`}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.externalReference}
                isClearable
                closeMenuOnSelect
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference="protocolDeviationBy"
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName="Protocol Deviation by:"
                />
              )}
            </div>
            <div className="error-message">
              {protocolDeviationByError && <div className="small-red-text">{protocolDeviationByError}</div>}
            </div>
          </>
        )}

        {missedVisitCrf && (
          <>
            <div className="input-container" style={{ marginTop: '5px' }}>
              <label className="input-label-right">Missed Visit CRF Date:</label>
              <DatePicker
                selected={missedVisitCRFDate}
                className={`text-input ${missedVisitCRFDateError && 'error-border'}`}
                dateFormat="dd-MM-yyyy"
                showPopperArrow={false}
                dropdownMode="select"
                placeholderText="Select..."
                disabled={fieldsDisabled}
                onChange={(selectedDate) => setMissedVisitCRFDate(selectedDate)}
                showYearDropdown
                showMonthDropdown
              />
              <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference="missedVisitCRFDate"
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName="Missed Visit CRF Date:"
                />
              )}
            </div>
            <div className="error-message">
              {missedVisitCRFDateError && <div className="small-red-text">{missedVisitCRFDateError}</div>}
            </div>

            <div className="input-container" style={{ marginTop: '5px' }}>
              <label className="input-label-right">Missed Visit CRF by:</label>
              <Select
                options={personList}
                onChange={setMissedVisitCRFBy}
                value={missedVisitCRFBy}
                isDisabled={fieldsDisabled}
                classNamePrefix={`input-select${missedVisitCRFByError ? 'error-border' : ''}`}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.externalReference}
                closeMenuOnSelect
                isClearable
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference="missedVisitCRFBy"
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName="Missed Visit CRF by:"
                />
              )}
            </div>
            <div className="error-message">
              {missedVisitCRFByError && <div className="small-red-text">{missedVisitCRFByError}</div>}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="past-visit">
      {visit.missedVisit && <p className="past-visit-header">Missed Visit</p>}

      <div className="past-visit-form">
        <div className={'form-section-left'}>
          <div className="input-container" style={{ marginTop: '5px' }}>
            <label className="input-label">Visit Date: </label>
            <DatePicker
              selected={visitDate}
              className={'text-input'}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              disabled
              dropdownMode="select"
              placeholderText="Select..."
              onChange={(selectedDate) => setVisitDate(selectedDate)}
            />
            <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'captureVisitDate123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Visit Date:'}
              />
            )}
          </div>
          <div className="input-container">
            <label className="input-label">Visit Code: </label>
            <input
              className="text-input"
              type="text"
              disabled
              value={visitCode}
              onChange={({ target }) => {
                setVisitCode(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'captureVisitCode123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Visit Code:'}
              />
            )}
          </div>
          <div className="input-container">
            <label className="input-label">Sub-Visit Code: </label>
            <input
              className="text-input"
              type="text"
              disabled
              value={subVisitCode}
              onChange={({ target }) => {
                setSubVisitCode(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'captureSubVisitCode123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Sub-Visit Code:'}
              />
            )}
          </div>
          <div className="input-container">
            <label className="input-label">Visit Type: </label>
            <input
              className="text-input"
              type="text"
              disabled
              value={visitType}
              onChange={({ target }) => {
                setVisitType(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'captureVisitType123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Visit Type:'}
              />
            )}
          </div>

          <div className="input-container">
            <label className="input-label">Forms: </label>
            <textarea
              className="input-area-list"
              rows={formsList?.length}
              value={formsList?.map((form) => form).join('\n')}
              disabled
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'captureVisitForms123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Forms:'}
              />
            )}
          </div>
        </div>
        <div className={'form-section-right'}>
          {visit.missedVisit ? renderMissedVisitFields() : renderDataCaptureFields()}

          <div className="input-container" style={{ marginTop: '5px' }}>
            <label className="input-label-right">SDV Needed: </label>
            <Checkbox
              label="Yes"
              fillStyle={true}
              checked={sdvNeeded}
              onChange={() => handleCheckboxChange(true)}
              disabled={fieldsDisabled}
              color="#000000"
            />
            <Checkbox
              label="No"
              fillStyle={true}
              checked={!sdvNeeded}
              onChange={() => handleCheckboxChange(false)}
              disabled={fieldsDisabled}
              color="#000000"
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'sdvNeeded123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'SDV Needed:'}
              />
            )}
          </div>
          {sdvNeeded && (
            <>
              <p className="sdv-message">
                Please note: The following fields are for the person conducting the SDV at a later time.
              </p>
              <div className="input-container" style={{ marginTop: '5px' }}>
                <label className="input-label-right">SDV Completed on: </label>
                <DatePicker
                  selected={sdvCapturedDate}
                  className={`text-input ${sdvCapturedDateError && 'error-border'}`}
                  dateFormat="dd-MM-yyyy"
                  showPopperArrow={false}
                  showYearDropdown
                  showMonthDropdown
                  disabled={fieldsDisabled}
                  dropdownMode="select"
                  placeholderText="Select..."
                  onChange={(selectedDate) => setSdvCapturedDate(selectedDate)}
                />
                <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
                {toolTipsEnabled && (
                  <ToolTip
                    tooltipExternalReference={'sdvCapturedData123'}
                    context={context}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    fieldName={'SDV Completed on:'}
                  />
                )}
              </div>
              <div className="error-message">
                {sdvCapturedDateError && <div className="small-red-text">{sdvCapturedDateError}</div>}
              </div>
              <div className="input-container" style={{ marginTop: '5px' }}>
                <label className="input-label-right">SDV Completed by: </label>
                <Select
                  options={personList}
                  onChange={setSdvCapturedBy}
                  value={sdvCapturedBy || null}
                  isDisabled={fieldsDisabled}
                  classNamePrefix={`input-select${sdvCapturedByError && 'error-border'}`}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.externalReference}
                  closeMenuOnSelect
                  isClearable
                />
                {toolTipsEnabled && (
                  <ToolTip
                    tooltipExternalReference={'sdvCapturedBy123'}
                    context={context}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    fieldName={'SDV Completed by:'}
                  />
                )}
              </div>
              <div className="error-message">
                {sdvCapturedByError && <div className="small-red-text">{sdvCapturedByError}</div>}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="past-visit-button-container">
        <SaveButton
          textColor="#74a47f"
          border="1px solid #74a47f"
          title="Delete Visit"
          onClick={handleDeleteVisitClick}
          color="#ffffff"
          disabled={fieldsDisabled}
        />
        <SaveButton
          textColor="#74a47f"
          border="1px solid #74a47f"
          title="Cancel"
          onClick={handleCancelClick}
          color="#ffffff"
        />
        <SaveButton
          textColor="#ffffff"
          border="1px solid #74a47f"
          title="Save Changes"
          onClick={handleSaveClick}
          color="#74a47f"
          disabled={fieldsDisabled}
        />
      </div>
    </div>
  );
};
